import { useCallback, useState } from "react";

import type { Action } from "app/services/types.generated";
import { missionHasUpcomingUndock } from "lib/mission/dockingMissionSteps";
import {
    collectPayloadHealthIssues,
    type PayloadHealthIssueId,
} from "lib/mission/payloadHealthCheck";
import { useFeatures } from "providers/FeaturesProvider";
import { useInsta360Session } from "providers/Insta360SessionProvider";
import { useMissionExecution } from "providers/navigation/MissionExecutionProvider";

type PendingStart = {
    missionId: string;
    startStepIndex?: number;
    skipDockedGate?: boolean;
};

export function usePayloadHealthMissionStart() {
    const { hasGimbalVideo, hasGimbalControl, hasArm } = useFeatures();
    const { fsm: insta360Fsm } = useInsta360Session();
    const { executeMission } = useMissionExecution();

    const [warningVisible, setWarningVisible] = useState(false);
    const [warningIssues, setWarningIssues] = useState<PayloadHealthIssueId[]>([]);
    const [pendingStart, setPendingStart] = useState<PendingStart | null>(null);

    const runPendingStart = useCallback(
        (pending: PendingStart) => {
            const options: { startStepIndex?: number; skipDockedGate?: boolean } = {};
            if (pending.startStepIndex != null && pending.startStepIndex > 0) {
                options.startStepIndex = pending.startStepIndex;
            }
            if (pending.skipDockedGate) {
                options.skipDockedGate = true;
            }
            executeMission(
                pending.missionId,
                Object.keys(options).length > 0 ? options : undefined,
            );
        },
        [executeMission],
    );

    const requestMissionStart = useCallback(
        (missionId: string, actions: Action[], options?: { startStepIndex?: number }) => {
            const startStepIndex = Math.max(0, Math.floor(options?.startStepIndex ?? 0));
            const skipDockedGate = missionHasUpcomingUndock(actions, startStepIndex);
            const issues = collectPayloadHealthIssues(actions, startStepIndex, {
                hasGimbalVideo,
                hasGimbalControl,
                insta360Fsm,
                hasArm,
            });
            const execOptions = {
                ...(options?.startStepIndex != null ? { startStepIndex: options.startStepIndex } : {}),
                ...(skipDockedGate ? { skipDockedGate: true } : {}),
            };
            if (issues.length === 0) {
                executeMission(
                    missionId,
                    Object.keys(execOptions).length > 0 ? execOptions : undefined,
                );
                return;
            }
            setPendingStart({ missionId, startStepIndex, skipDockedGate });
            setWarningIssues(issues);
            setWarningVisible(true);
        },
        [executeMission, hasArm, hasGimbalControl, hasGimbalVideo, insta360Fsm],
    );

    const continueDespiteWarning = useCallback(() => {
        const pending = pendingStart;
        setWarningVisible(false);
        setWarningIssues([]);
        setPendingStart(null);
        if (pending) runPendingStart(pending);
    }, [pendingStart, runPendingStart]);

    const cancelPayloadWarning = useCallback(() => {
        setWarningVisible(false);
        setWarningIssues([]);
        setPendingStart(null);
    }, []);

    return {
        requestMissionStart,
        payloadWarningVisible: warningVisible,
        payloadWarningIssues: warningIssues,
        continueDespitePayloadWarning: continueDespiteWarning,
        cancelPayloadWarning,
    };
}
